import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
//import { useNavigate } from 'react-router-dom';
import { projectService } from '../services/projectService';
import { getProjectIdentityFields } from '../utils/projectIdentityFields';
import { parsePolicyDenialResponse } from '../utils/policyViewerDenial';

const ProjectContext = createContext();

export const useProject = () => {
    const context = useContext(ProjectContext);
    if (!context) {
        throw new Error('useProject must be used within a ProjectProvider');
    }
    return context;
};

export const ProjectProvider = ({ children }) => {
    const [currentProject, setCurrentProject] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [deniedReason, setDeniedReason] = useState(null);
    //const navigate = useNavigate();

    const loadProject = async (projectId) => {
        if (!projectId) {
            setError('No project ID provided');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setDeniedReason(null);
            const response = await projectService.getProject(projectId);

            if (!response || !response.data) {
                throw new Error('Invalid response from server');
            }

            setCurrentProject(response.data);
            localStorage.setItem('currentProjectId', projectId);
        } catch (err) {
            console.error('Error loading project:', err);
            const denial = parsePolicyDenialResponse(err, {
                defaultAction: 'study:open',
                forbiddenFallbackReason: 'You do not have access to this study.',
            });
            if (denial) {
                setDeniedReason(denial.reason);
            } else {
                setError(err.message || 'Failed to load project');
            }
            setCurrentProject(null);
            localStorage.removeItem('currentProjectId');
        } finally {
            setLoading(false);
        }
    };

    const clearProject = () => {
        setCurrentProject(null);
        setError(null);
        setDeniedReason(null);
        localStorage.removeItem('currentProjectId');
    };

    // Load project from localStorage on initial mount — but ONLY when the
    // current URL doesn't already name a project. Most pages (StudyFormTabs.jsx's
    // "Hub IAM policy gate" effect, among others) call loadProject(projectIdFromUrl)
    // themselves as soon as they mount, for the study actually being viewed.
    // Firing this localStorage-driven load unconditionally raced that call: both
    // write the same shared `currentProject` state, so whichever async
    // projectService.getProject() resolved LAST won — even if it was for a
    // stale id left over from a previous session. If that stale id no longer
    // resolves (deleted project, bad format), its failure handler below wins
    // the race instead and wipes/nulls out the correct, already-loaded project,
    // surfacing as a spurious "Invalid projectId" error on the current page.
    useEffect(() => {
        const hasProjectInUrl = new URLSearchParams(window.location.search).has('project');
        if (hasProjectInUrl) return;
        const savedProjectId = localStorage.getItem('currentProjectId');
        if (savedProjectId) {
            loadProject(savedProjectId).catch(err => {
                console.error('Error loading saved project:', err);
                clearProject();
            });
        }
    }, []);

    const identity = useMemo(
        () => getProjectIdentityFields(currentProject),
        [currentProject]
    );

    const value = {
        currentProject,
        identity,
        loading,
        error,
        deniedReason,
        loadProject,
        clearProject
    };

    return (
        <ProjectContext.Provider value={value}>
            {children}
        </ProjectContext.Provider>
    );
};

export default ProjectContext; 